import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import BadgeTradeDirection from 'src/components/Badge/BadgeTradeDirection';
import BadgeTradePointType from 'src/components/Badge/BadgeTradePointType';
import BadgeTradeRuleState from 'src/components/Badge/BadgeTradeRuleState';
import BadgeTradeType from 'src/components/Badge/BadgeTradeType';
import Loading from 'src/components/Loading';
import UUID from 'src/components/UUID';
import { useTradeRuleCounterParty } from 'src/hooks/useTradeRuleCounterParty';
import { getNodesFromEdges } from 'src/util/graphql';
import { useIntlCurrency } from 'src/util/i18n/currency';
import displayUserName from 'src/util/i18n/decorators/displayUserName';
import { valuePerUnitWithStrong } from 'src/util/i18n/energyPrice';
import buildTimeRangeSummary from 'src/util/i18n/timeRange';

import AdminMeterTradeRuleTableItemClause from '../AdminMeterTradeRulesTableItemClause';

const AdminMeterShowTradeRuleFragment = graphql`
  fragment AdminMeterShowTradeRule_rule on TradeRule {
    uuid
    buyer {
      userId
      user { uuid email givenName familyName }
      community { uuid title }
      residualId
      tradePoint { uuid type }
    }
    seller {
      userId
      user { uuid email givenName familyName }
      community { uuid title }
      residualId
      tradePoint { uuid type }
    }
    clauses {
      edges {
        node {
          uuid
          price
          ignoreDaylightSavings
          ignorePublicHolidays
          timezone
          ...AdminMeterTradeRulesTableItemClause_clause
        }
      }
    }
    tradeType
    state
    start
    finish
    proposedAt
    proposedBy { uuid email givenName familyName }
    acceptedAt
    acceptedBy { uuid email givenName familyName }
    rejectedAt
    rejectedBy { uuid email givenName familyName }
    cancelledAt
    cancelledBy { uuid email givenName familyName }
    closedAt
    closedBy { uuid email givenName familyName }
  }
`;

/**
 * @param {object} props
 * @returns {React.ReactComponentElement} - AdminMeterShowTradeRule component
 */
function AdminMeterShowTradeRule(props) {
  if (!props || Object.keys(props).length === 0) {
    return <Loading />;
  }

  let { rule } = props;
  rule = useFragment(AdminMeterShowTradeRuleFragment, rule);

  const { propertyId, tradePointId } = props;
  const intl = useIntl();
  const intlCurrency = useIntlCurrency();

  const {
    uuid, tradeType, state, start, finish,
  } = rule;
  const active = { start, finish };
  const clauses = getNodesFromEdges(rule.clauses).sort((a, b) => (b.price - a.price));

  const {
    direction, trader, userId, tradePoint,
  } = useTradeRuleCounterParty(rule, tradePointId) || {};

  const userName = displayUserName(intl, trader, userId);

  return (
    <tr key={`trade-rule-${uuid}`}>
      <th>
        <UUID uuid={uuid} url={`/properties/${propertyId}/trade-rules/${uuid}`} />
        <br />
        <BadgeTradeDirection direction={direction} />
        <br />
        <BadgeTradeType type={tradeType} />
        <br />
        <BadgeTradeRuleState state={state} />
      </th>
      <td>
        {userName && (
          <>
            <strong>{userName}</strong>
            <br />
            <UUID uuid={trader?.uuid ?? userId} />
          </>
        )}
      </td>
      <td>
        {tradePoint && (
          <>
            <strong>{tradePoint.label}</strong>
            <br />
            <UUID uuid={tradePoint.tradePoint.uuid} />
            <br />
            <BadgeTradePointType type={tradePoint.tradePoint.type} />
          </>
        )}
      </td>
      <td>
        {clauses.length === 0 && (
          <p style={{ display: 'flex' }}>
            {valuePerUnitWithStrong(intl, intlCurrency, undefined)}
          </p>
        )}
        {clauses.length > 0 && (
        <ul className="ps-2">
          {clauses && clauses.map((clause) => (
            <AdminMeterTradeRuleTableItemClause
              clause={clause}
              key={clause.uuid}
              showConditions={false}
            />
          ))}
        </ul>
        )}
      </td>
      <td>
        {buildTimeRangeSummary(intl, active)}
      </td>
    </tr>
  );
}

export default AdminMeterShowTradeRule;

AdminMeterShowTradeRule.propTypes = {
  propertyId: PropTypes.string.isRequired,
  rule: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  tradePointId: PropTypes.string.isRequired,
};
